import React, { useCallback } from "react";
import AsyncCreatableSelect from "react-select/async-creatable";
import debounce from "lodash.debounce";
import { getSkillsDropdown } from "../../../services/skills/skills-services";
import { primaryColor } from "../styles/color";

const OTHER_OPTION = { value: "__other__", label: "Other (type your own skill)" };

const FALLBACK_SKILL_NAMES = [
  "JavaScript",
  "TypeScript",
  "React",
  "Node.js",
  "HTML",
  "CSS",
  "WordPress",
  "PHP",
  "Python",
  "UI Design",
  "UX Design",
  "Figma",
  "SEO",
  "Content Writing",
  "Project Management",
  "Data Analysis",
  "Mobile Development",
  "Graphic Design",
  "Video Editing",
  "Digital Marketing",
];

const toFallbackOptions = () =>
  FALLBACK_SKILL_NAMES.map((name) => ({
    value: `custom:${name}`,
    label: name,
  }));

const mergeWithOther = (options) => {
  const list = Array.isArray(options) ? [...options] : [];
  if (!list.some((o) => o.value === OTHER_OPTION.value)) {
    list.push(OTHER_OPTION);
  }
  return list;
};

const SkillsDropdown = ({
  value = [],
  onChange,
  placeholder = "Select or type skills...",
}) => {
  const fetchSkills = async (inputValue) => {
    try {
      const res = await getSkillsDropdown(inputValue);

      if (!res || res.status !== 200) {
        return mergeWithOther(toFallbackOptions());
      }

      const data = res.data?.result?.data;
      if (!Array.isArray(data) || data.length === 0) {
        const fallback = toFallbackOptions().filter((opt) =>
          !inputValue
            ? true
            : opt.label.toLowerCase().includes(inputValue.toLowerCase()),
        );
        return mergeWithOther(fallback);
      }

      const options = data.map((skill) => ({
        value: skill.id,
        label: skill.skill_name,
      }));

      return mergeWithOther(options);
    } catch (error) {
      console.error("Error fetching skills:", error);
      return mergeWithOther(toFallbackOptions());
    }
  };

  const loadOptions = useCallback(
    debounce((inputValue, callback) => {
      fetchSkills(inputValue).then(callback);
    }, 300),
    [],
  );

  const handleChange = (selected, actionMeta) => {
    if (
      actionMeta?.action === "select-option" &&
      actionMeta?.option?.value === "__other__"
    ) {
      return;
    }

    const next = (selected || [])
      .filter((item) => item.value !== "__other__")
      .map((item) => {
        if (item.__isNew__) {
          return { value: `custom:${item.label}`, label: item.label };
        }
        return item;
      });
    onChange(next);
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: "0.5rem",
      boxShadow: "none",
      minHeight: "48px",
      fontSize: "16px",
      "&:hover": {
        borderColor: primaryColor,
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "6px 10px",
    }),
    input: (base) => ({
      ...base,
      fontSize: "16px",
      margin: 0,
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: "16px",
      color: "#64748b",
    }),
    menu: (base) => ({
      ...base,
      fontSize: "16px",
      zIndex: 20,
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "16px",
      padding: "10px 12px",
      fontStyle: state.data?.value === "__other__" ? "italic" : "normal",
      color: state.data?.value === "__other__" ? "#64748b" : base.color,
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: primaryColor,
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#fff",
      fontSize: "15px",
      padding: "4px 6px",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#fff",
      ":hover": {
        backgroundColor: "rgba(255,255,255,0.2)",
        color: "#fff",
      },
    }),
  };

  return (
    <AsyncCreatableSelect
      isMulti
      cacheOptions
      defaultOptions
      loadOptions={loadOptions}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      formatCreateLabel={(input) => `Add skill: "${input}"`}
      isValidNewOption={(input) => Boolean(input?.trim())}
      noOptionsMessage={({ inputValue }) =>
        inputValue
          ? `No match for "${inputValue}" — press Enter to add it`
          : "Start typing to search skills"
      }
      styles={selectStyles}
    />
  );
};

export default SkillsDropdown;
